import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import TrackedLink from "@/components/TrackedLink";
import {
  ArrowLeft, ArrowRight, Mail, HardDrive, Users, FolderOpen, Globe, Shield,
  CheckCircle2, Monitor, Laptop, Lock, Eye, RefreshCw, Phone, MonitorSmartphone,
  MapPin, Smartphone, KeyRound, ShieldCheck, Building2, GraduationCap, HardHat,
} from "lucide-react";
import { motion } from "framer-motion";

import managedHeroBg from "@/assets/managed-hero-bg.jpg";
import outlookIcon from "@/assets/ms-icons/outlook.svg";
import onedriveIcon from "@/assets/ms-icons/onedrive.svg";
import teamsIcon from "@/assets/ms-icons/teams.svg";
import sharepointIcon from "@/assets/ms-icons/sharepoint.svg";
import wordIcon from "@/assets/ms-icons/word.svg";
import excelIcon from "@/assets/ms-icons/excel.svg";
import powerpointIcon from "@/assets/ms-icons/powerpoint.svg";
import onenoteIcon from "@/assets/ms-icons/onenote.svg";
import formsIcon from "@/assets/ms-icons/forms.svg";
import plannerIcon from "@/assets/ms-icons/planner.svg";
import bookingsIcon from "@/assets/ms-icons/bookings.svg";
import defenderIcon from "@/assets/ms-icons/defender.svg";
import intuneIcon from "@/assets/ms-icons/intune.svg";

type FeatureItem = {
  icon: string;
  text: string;
};

const features: FeatureItem[] = [
  { icon: outlookIcon, text: "Zakelijke e-mail met Exchange Online (50 GB per mailbox)" },
  { icon: onedriveIcon, text: "1 TB OneDrive-opslag per gebruiker" },
  { icon: teamsIcon, text: "Microsoft Teams voor chatten, bellen en videobellen" },
  { icon: sharepointIcon, text: "SharePoint Online voor documentbeheer en samenwerking" },
  { icon: wordIcon, text: "Word (desktop-, web- en mobiele versie)" },
  { icon: excelIcon, text: "Excel (desktop-, web- en mobiele versie)" },
  { icon: powerpointIcon, text: "PowerPoint (desktop-, web- en mobiele versie)" },
  { icon: onenoteIcon, text: "OneNote (desktop-, web- en mobiele versie)" },
  { icon: outlookIcon, text: "Outlook desktop-app volledig geïnstalleerd" },
  { icon: bookingsIcon, text: "Microsoft Bookings voor afsprakenbeheer" },
  { icon: formsIcon, text: "Microsoft Forms" },
  { icon: plannerIcon, text: "Microsoft Planner en Lists" },
  { icon: defenderIcon, text: "Microsoft Defender for Business" },
  { icon: intuneIcon, text: "Apparaatbeheer via Microsoft Intune" },
];

const highlights = [
  {
    icon: Laptop,
    title: "Volledige Office-apps",
    text: "Werk met de desktop-versies van Word, Excel, PowerPoint en Outlook. Alles geïnstalleerd en altijd up-to-date.",
    color: "bg-hero-gradient",
  },
  {
    icon: Shield,
    title: "Geavanceerde beveiliging",
    text: "Microsoft Defender beschermt tegen virussen, ransomware en phishing. Apparaatbeheer via Intune zorgt dat ook verloren apparaten veilig zijn.",
    color: "bg-rinqel-blue-gradient",
  },
  {
    icon: Lock,
    title: "Identiteit & toegangsbeheer",
    text: "Tweestapsverificatie, voorwaardelijke toegang en beveiligde inlog. Alleen de juiste mensen bij de juiste gegevens.",
    color: "bg-rinqel-green-gradient",
  },
  {
    icon: MonitorSmartphone,
    title: "Proactief beheer",
    text: "Wij monitoren je werkplekken op afstand met gespecialiseerde beheersoftware. Problemen lossen we op voordat je ze merkt.",
    color: "bg-hero-gradient",
  },
  {
    icon: RefreshCw,
    title: "Altijd up-to-date",
    text: "Updates en beveiligingspatches worden automatisch uitgerold. Je werkt altijd met de nieuwste en veiligste versie.",
    color: "bg-rinqel-blue-gradient",
  },
  {
    icon: Phone,
    title: "Persoonlijke support",
    text: "Geen ticketsysteem, geen wachtrijen. Bij ons bel je gewoon en heb je direct iemand met verstand van zaken aan de lijn.",
    color: "bg-rinqel-green-gradient",
  },
];

const WerkplekManaged = () => {
  return (
    <>
      {/* Hero */}
      <section className="relative min-h-[480px] overflow-hidden">
        <img
          src={managedHeroBg}
          alt="Professioneel team werkt samen op managed werkplekken"
          loading="eager" decoding="async"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/55 to-black/25" />
        <div className="container relative z-10 flex min-h-[480px] items-center py-20">
          <div className="max-w-2xl">
            <Link
              to="/diensten/werkplek"
              className="mb-6 inline-flex items-center gap-1 text-sm text-white/70 hover:text-white transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              Terug naar Digitale Werkplek
            </Link>

            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
              <span className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-1.5 text-xs font-semibold text-white backdrop-blur-sm">
                <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                Op basis van Microsoft 365 Business Premium
              </span>
            </motion.div>

            <motion.div
              className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-white/15 backdrop-blur-sm border border-white/20"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.05 }}
            >
              <Monitor className="h-7 w-7 text-white" />
            </motion.div>

            <motion.h1
              className="text-4xl font-bold leading-[1.1] tracking-tight text-white md:text-5xl"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              Managed{" "}
              <span className="text-gradient">Werkplek</span>
            </motion.h1>

            <motion.p
              className="mt-5 max-w-xl text-lg leading-relaxed text-white/80"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              De complete werkplek met alle Office-apps, geavanceerde beveiliging en proactief beheer door Rinqel. Alles wat je nodig hebt, niets waar je omkijken naar hebt.
            </motion.p>

            <motion.div
              className="mt-8"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <span className="text-4xl font-bold text-white">€50</span>
              <span className="ml-1 text-white/70">/per maand per gebruiker</span>
            </motion.div>

            <motion.div
              className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-white/70"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <span className="flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                Volledige Office-apps inbegrepen
              </span>
              <span className="flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-rinqel-blue" />
                Microsoft Defender beveiliging
              </span>
              <span className="flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-rinqel-green" />
                Proactief beheer door Rinqel
              </span>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Wat zit erin */}
      <section className="py-16 md:py-20">
        <div className="container">
          <motion.div
            className="mx-auto max-w-3xl text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-2xl font-bold md:text-3xl">
              Alles in één pakket —{" "}
              <span className="text-gradient">door ons ingericht en beheerd</span>
            </h2>
            <p className="mt-4 text-lg leading-relaxed text-muted-foreground">
              De Managed Werkplek combineert Microsoft 365 Business Premium met proactief beheer door Rinqel.
              Wij richten alles voor je in, houden het draaiend en lossen problemen op — vaak voordat je ze merkt.
              Jij hoeft je alleen bezig te houden met waar je goed in bent.
            </p>
          </motion.div>

          <div className="mx-auto mt-10 max-w-3xl">
            <div className="rounded-2xl border border-border bg-card p-6 md:p-8 shadow-card">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {features.map((f, i) => (
                  <motion.div
                    key={f.text}
                    className="flex items-center gap-3 rounded-lg border border-border/50 bg-muted/30 px-4 py-3"
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.3, delay: i * 0.04 }}
                  >
                    <img loading="lazy" decoding="async"
                      src={f.icon}
                      alt={f.text.split("(")[0].trim()}
                      className="h-8 w-8 shrink-0"
                    />
                    <span className="text-sm">{f.text}</span>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Highlights */}
      <section className="bg-muted/40 py-16 md:py-20">
        <div className="container">
          <motion.h2
            className="mb-12 text-center text-2xl font-bold md:text-3xl"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            Waarom de Managed Werkplek{" "}
            <span className="text-gradient">zo lekker werkt</span>
          </motion.h2>

          <div className="mx-auto grid max-w-5xl gap-5 md:grid-cols-2">
            {highlights.map((item, i) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={item.title}
                  className="flex gap-4 rounded-xl border border-border bg-card p-6 shadow-card transition-shadow hover:shadow-card-hover"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.06 }}
                >
                  <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-lg ${item.color} text-primary-foreground`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="font-semibold">{item.title}</h3>
                    <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{item.text}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Defender / Beveiliging */}
      <section className="py-16 md:py-20">
        <div className="container">
          <motion.div
            className="mx-auto max-w-3xl text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-destructive/20 bg-destructive/5 px-4 py-1.5 text-xs font-semibold text-destructive">
              <Shield className="h-3.5 w-3.5" />
              Exclusief bij de Managed Werkplek
            </div>
            <h2 className="text-2xl font-bold md:text-3xl">
              Beveiliging die écht{" "}
              <span className="text-gradient">meedenkt</span>
            </h2>
            <p className="mt-4 text-lg leading-relaxed text-muted-foreground">
              Hackers worden steeds slimmer. Gelukkig jouw beveiliging ook. Met Microsoft Defender XDR
              zit er een compleet AI-beveiligingsteam in je werkplek ingebouwd. Wij richten het in, monitoren het 
              en grijpen in als het nodig is. Jij merkt er niks van — behalve dat alles veilig is.
            </p>
          </motion.div>

          <div className="mx-auto mt-10 grid max-w-5xl gap-5 md:grid-cols-2">
            {[
              {
                icon: Monitor,
                title: "Bescherming van je apparaten",
                text: "Malware, ransomware, verdachte bestanden — Defender for Endpoint herkent het en grijpt automatisch in. Wij houden het in de gaten, zodat jij gewoon kunt doorwerken.",
                color: "bg-hero-gradient",
              },
              {
                icon: Mail,
                title: "Veilig mailen en samenwerken",
                text: "Phishing-mails, foute linkjes, gevaarlijke bijlagen in Teams of SharePoint? Defender for Office 365 vangt ze op. Inclusief slimme AI die nep-facturen herkent.",
                color: "bg-rinqel-blue-gradient",
              },
              {
                icon: Users,
                title: "Wie is wie? Identiteitsbeveiliging",
                text: "Iemand probeert in te loggen als jouw collega? Defender for Identity herkent verdacht gedrag en blokkeert het. Geen kans voor binnengedrongen indringers.",
                color: "bg-rinqel-green-gradient",
              },
              {
                icon: Globe,
                title: "Controle over je cloud-apps",
                text: "Welke apps gebruiken je medewerkers? Defender for Cloud Apps houdt het overzicht, bewaakt je data en signaleert risico's. Handig als je niet alles zelf wilt checken (en wie wil dat?).",
                color: "bg-hero-gradient",
              },
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={item.title}
                  className="flex gap-4 rounded-xl border border-border bg-card p-6 shadow-card transition-shadow hover:shadow-card-hover"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.06 }}
                >
                  <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-lg ${item.color} text-primary-foreground`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="font-semibold">{item.title}</h3>
                    <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{item.text}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>

          <motion.div
            className="mx-auto mt-10 max-w-3xl"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="rounded-2xl border border-border bg-card p-6 md:p-8 shadow-card">
              <h3 className="mb-4 text-lg font-bold">En wat levert dat op? 🎯</h3>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {[
                  { text: "Phishing-links worden automatisch verwijderd — ook als een medewerker er tóch op klikt, is de link al onschadelijk gemaakt", icon: Eye },
                  { text: "Bijlagen met virussen worden uit je mail gefilterd nog vóórdat je ze ziet — klikken op iets gevaarlijks kan simpelweg niet meer", icon: Shield },
                  { text: "AI in de cloud speurt continu naar verdacht gedrag en grijpt in — zonder dat jij of je team er iets voor hoeft te doen", icon: RefreshCw },
                  { text: "Gewoon veiliger werken, zonder extra gedoe — kwaadwillenden blijven buiten de deur, jij focust op je werk", icon: CheckCircle2 },
                ].map((item, i) => {
                  const Icon = item.icon;
                  return (
                    <motion.div
                      key={item.text}
                      className="flex items-start gap-3 rounded-lg border border-border/50 bg-muted/30 px-4 py-3"
                      initial={{ opacity: 0, y: 10 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.3, delay: i * 0.05 }}
                    >
                      <Icon className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                      <span className="text-sm">{item.text}</span>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </motion.div>

          {/* Conditional Access */}
          <motion.div
            className="mx-auto mt-16 max-w-5xl"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="text-center mb-10">
              <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/5 px-4 py-1.5 text-xs font-semibold text-primary">
                <KeyRound className="h-3.5 w-3.5" />
                Inbegrepen bij de Managed Werkplek
              </div>
              <h2 className="text-2xl font-bold md:text-3xl">
                Conditional Access:{" "}
                <span className="text-gradient">jouw digitale portier</span>
              </h2>
              <p className="mx-auto mt-4 max-w-2xl text-lg leading-relaxed text-muted-foreground">
                Stel je voor: een slimme portier die bij elke inlogpoging checkt of alles klopt. Waar log je in? 
                Vanaf welk apparaat? Is het verdacht? Wij stellen alle regels voor je in en passen ze aan wanneer nodig.
                Jouw medewerkers merken er niks van — behalve dat alles gewoon werkt.
              </p>
            </div>

            <div className="rounded-2xl border border-border bg-card p-6 md:p-8 shadow-card mb-8">
              <h3 className="mb-2 text-lg font-bold">Hoe werkt het? 🔧</h3>
              <p className="mb-5 text-sm leading-relaxed text-muted-foreground">
                Wij stellen samen met jou de regels op en richten alles in. Bijvoorbeeld: <em>"Als iemand van buiten Europa inlogt → verplicht extra verificatie"</em> of <em>"Vanaf een onbeheerd apparaat → geen downloads toestaan"</em>. 
                Daarna bewaken en onderhouden wij het — jij hebt er geen omkijken naar.
              </p>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {[
                  { icon: MapPin, title: "Locatie", text: "Alleen toegang vanuit Nederland? Of buiten Europa blokkeren? Wij stellen het voor je in — jij geeft alleen aan wat je wilt.", color: "bg-hero-gradient" },
                  { icon: MonitorSmartphone, title: "Apparaatstatus", text: "Beheerd via Intune? Welkom. Privé-laptop? Alleen beperkte webtoegang of helemaal blokkeren. Wij regelen de inrichting.", color: "bg-rinqel-blue-gradient" },
                  { icon: Shield, title: "Risiconiveau", text: "Verdachte login gedetecteerd? Automatisch MFA afdwingen of de toegang blokkeren. Wij monitoren het actief, zodat jij je nergens zorgen over hoeft te maken.", color: "bg-rinqel-green-gradient" },
                  { icon: Globe, title: "Per app instellen", text: "Bepaal per app wie er mag inloggen, wanneer en vanaf welk type apparaat. Wij configureren het en houden het up-to-date.", color: "bg-hero-gradient" },
                ].map((item, i) => {
                  const Icon = item.icon;
                  return (
                    <motion.div
                      key={item.title}
                      className="flex gap-4 rounded-xl border border-border/50 bg-muted/30 p-4"
                      initial={{ opacity: 0, y: 10 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.3, delay: i * 0.05 }}
                    >
                      <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${item.color} text-primary-foreground`}>
                        <Icon className="h-5 w-5" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-sm">{item.title}</h4>
                        <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{item.text}</p>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>

            {/* Voordelen */}
            <div className="grid gap-5 md:grid-cols-2 mb-8">
              {[
                { icon: ShieldCheck, title: "Sterkere beveiliging, nul gedoe", text: "Op kantoor? Gewoon doorwerken. Vanuit het buitenland? Even extra verifiëren. Onbekend apparaat? Geblokkeerd. Wij richten het in en onderhouden het — jouw medewerkers merken er bijna niks van, maar hackers wel.", color: "bg-hero-gradient" },
                { icon: Smartphone, title: "Geen toegang vanaf onbeheerde apparaten", text: "Privé-laptop of telefoon van een medewerker? Wij stellen in wat wel en niet mag: alleen webtoegang, downloads blokkeren of helemaal geen toegang. Zo lekt er nooit data via een onbeveiligd apparaat.", color: "bg-rinqel-blue-gradient" },
                { icon: Lock, title: "99% minder accounthacks", text: "Risicovolle logins blokkeren, MFA afdwingen, toegang beperken tot vertrouwde locaties — wij zetten het op en houden het scherp. Phishing en credential stuffing worden vrijwel volledig geneutraliseerd.", color: "bg-rinqel-green-gradient" },
                { icon: CheckCircle2, title: "Compliance op de automatische piloot", text: "MFA verplicht? Check. Apparaatbeheer? Check. Toegangsbeperkingen? Check. Wij richten het zo in dat je automatisch voldoet aan AVG, ISO en branche-eisen. Geen extra werk voor jou.", color: "bg-hero-gradient" },
                { icon: Globe, title: "Veilig hybride werken", text: "Thuiswerken, onderweg of op locatie — wij zorgen dat je medewerkers altijd veilig toegang hebben. Downloads blokkeren op persoonlijke apparaten? Door ons geregeld. Jij focust op je werk.", color: "bg-rinqel-blue-gradient" },
                { icon: Users, title: "Fijnmazige toegangscontrole", text: "Bepaal per gebruiker, per app en per apparaat wie waar bij mag. Wij configureren het en passen het aan als je organisatie groeit. Enterprise-niveau beveiliging, zonder dat je er zelf naar hoeft om te kijken.", color: "bg-rinqel-green-gradient" },
              ].map((item, i) => {
                const Icon = item.icon;
                return (
                  <motion.div
                    key={item.title}
                    className="flex gap-4 rounded-xl border border-border bg-card p-6 shadow-card transition-shadow hover:shadow-card-hover"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: i * 0.06 }}
                  >
                    <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-lg ${item.color} text-primary-foreground`}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <div>
                      <h3 className="font-semibold">{item.title}</h3>
                      <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{item.text}</p>
                    </div>
                  </motion.div>
                );
              })}
            </div>

            {/* Praktijkvoorbeelden */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="rounded-2xl border border-border bg-card p-6 md:p-8 shadow-card">
                <h3 className="mb-5 text-lg font-bold">Herkenbaar? Zo werkt het in de praktijk 💡</h3>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  {[
                    { icon: Building2, title: "Administratiekantoor", bullets: ["Alleen inloggen vanuit Nederland", "Buitenlandse logins? Geblokkeerd", "Onbeheerde apparaten → alleen webtoegang"] },
                    { icon: GraduationCap, title: "School of stichting", bullets: ["Leerkrachten: volledige toegang", "Tijdelijke krachten: alleen op locatie", "Leerlingen: beperkt tot schoolnetwerk"] },
                    { icon: HardHat, title: "Bouwbedrijf", bullets: ["Monteurs: alleen mobiele apps", "Privételefoon? MFA verplicht", "Desktop-apps beperkt tot kantoor"] },
                  ].map((example, i) => {
                    const Icon = example.icon;
                    return (
                      <motion.div
                        key={example.title}
                        className="rounded-xl border border-border/50 bg-muted/30 p-5"
                        initial={{ opacity: 0, y: 10 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.3, delay: i * 0.08 }}
                      >
                        <div className="mb-3 flex items-center gap-2">
                          <Icon className="h-5 w-5 text-primary" />
                          <h4 className="font-semibold text-sm">{example.title}</h4>
                        </div>
                        <ul className="space-y-1.5">
                          {example.bullets.map((b) => (
                            <li key={b} className="flex items-start gap-2 text-sm text-muted-foreground">
                              <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
                              {b}
                            </li>
                          ))}
                        </ul>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Verschil met Cloud Only */}
      <section className="bg-muted/40 py-16 md:py-20">
        <div className="container">
          <motion.div
            className="mx-auto max-w-3xl text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-2xl font-bold md:text-3xl">
              Verschil met <span className="text-gradient">Cloud Only?</span>
            </h2>
            <p className="mt-4 text-lg leading-relaxed text-muted-foreground">
              Bij de Cloud Only Werkplek werk je met de webversies van Office. Bij de Managed Werkplek krijg je
              de volledige desktop-apps (Word, Excel, PowerPoint, Outlook), geavanceerde beveiliging met Microsoft Defender,
              apparaatbeheer via Intune én proactief beheer door Rinqel. De complete oplossing voor bedrijven die maximale
              productiviteit en veiligheid willen.
            </p>
            <Button variant="hero-outline" size="lg" className="mt-6" asChild>
              <Link to="/diensten/werkplek/cloud-only">
                Bekijk Cloud Only Werkplek
                <ArrowRight className="ml-1 h-4 w-4" />
              </Link>
            </Button>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-muted/40 py-12">
        <div className="container">
          <div className="flex flex-col items-center gap-4 text-center sm:flex-row sm:justify-center">
            <Button variant="hero" size="lg" asChild>
              <TrackedLink to="/contact" trackLabel="Vraag aan" trackLocation="werkplek-managed-cta">
                Vraag aan
                <ArrowRight className="ml-1 h-4 w-4" />
              </TrackedLink>
            </Button>
            <Button variant="hero-outline" size="lg" asChild>
              <Link to="/diensten/werkplek">Terug naar overzicht</Link>
            </Button>
          </div>
        </div>
      </section>
    </>
  );
};

export default WerkplekManaged;
